import React, { useEffect, useRef, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useDispatch, useSelector } from 'react-redux';
import { apiRunWithAxios } from './APIRunWithAxios';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import { authLoginAndGetJWTToken } from '../../../../store/actions/OmniComm/OmniCommAuthAction';

const FuelVolumeWithEventsChart = ({ formData }) => {
    const dispatch = useDispatch();
    const { data } = useSelector(state => state.omniAuthLoginReducer);

    const rootRef = useRef(null);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [maxYAxis, setMaxYAxis] = useState(0);
    const [eventsData, setEventsData] = useState([]);

    useEffect(() => {
        if (formData?.asset_id && formData?.date_range_start && formData?.date_range_end) {
            getFuelValue();
            getIgnitionEvents();
        }
    }, [formData, data]);

    useEffect(() => {
        if (chartData.length > 0 || eventsData.length > 0) {
            createChart();
        }
        return () => {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
        };
    }, [chartData, eventsData]);

    const getFuelValue = async () => {
        if (data?.jwt) {
            setChartData([]);
            try {
                setLoading(true);
                let timeBegin, timeEnd;

                if (formData?.timeBegin && formData?.timeEnd) {
                    timeBegin = formData.timeBegin;
                    timeEnd = formData.timeEnd;
                } else if (formData?.date_range_start && formData?.date_range_end) {
                    timeBegin = Math.floor(new Date(formData.date_range_start).getTime() / 1000);
                    timeEnd = Math.floor(new Date(formData.date_range_end).getTime() / 1000);
                } else {
                    const now = Math.floor(Date.now() / 1000);
                    timeBegin = now - (3 * 24 * 60 * 60);
                    timeEnd = now;
                }
                // 336999007
                const vehicleID = formData?.asset_id;
                if (vehicleID && timeBegin && timeEnd) {
                    let params = {
                        timeBegin: timeBegin,
                        timeEnd: timeEnd,
                        vehicleID
                    };

                    let response = await apiRunWithAxios(
                        "https://online.omnicomm.in/ls/api/v1/reports/fuellevel",
                        "GET",
                        data?.jwt,
                        params,
                        {}
                    );
                    
                    const results = response.data;

                    if (results && results.tankData && results.tankData[0] && results.tankData[0].data) {
                        const transformedData = results.tankData[0].data
                            .filter(item => item.eD && (item.rV !== undefined || item.aV !== undefined))
                            .map(item => ({
                                date: item.eD * 1000,
                                rV: item.rV || 0,
                                aV: item.aV || 0
                            }))
                            .sort((a, b) => a.date - b.date);

                        setChartData(transformedData);
                        const maxValue = Math.max(...transformedData.map(item => item.rV));
                        setMaxYAxis(Math.ceil(maxValue * 1.1));
                    } else {
                        toast.info('No fuel data available', { position: toast.POSITION.TOP_RIGHT });
                    }
                }
            } catch (error) {
                loginAgain(error)
            } finally {
                setLoading(false);
            }
        }
    };

    const loginAgain = (error) => {
        if(error?.response?.status === 401 && error?.response?.data?.error === "Unauthorized") {
            swal({
                title: "Refresh",
                text: "Please refresh again for fetch graph data.",
                icon: "warning",
                // dangerMode: true,
                buttons: ["Cancel", "Refresh"],
            }).then(willDelete => {
                if (willDelete) {
                    dispatch(authLoginAndGetJWTToken());
                }
            });
        }
    }

    const getEventsData = async (type) => {
        const timeBegin = Math.floor(new Date(formData.date_range_start + " 00:00:00").getTime() / 1000);
        const timeEnd = Math.floor(new Date(formData.date_range_end + " 23:59:59").getTime() / 1000);

        if (formData.uuid && timeBegin && timeEnd) {
            try {
                const params = { timeBegin, timeEnd, type };
                // 336999007
                let response = await apiRunWithAxios(
                    `https://online.omnicomm.in/ls/api/v1/reports/events/${formData.uuid}`,
                    "GET",
                    data?.jwt,
                    params
                );
                return response.data?.events || [];
            } catch (error) {
                loginAgain(error)
                return [];
            }
        }
        return [];
    };

    const getIgnitionEvents = async () => {
        try {
            const onDataRaw = await getEventsData(5);
            const offDataRaw = await getEventsData(6);

            const onData = (onDataRaw || []).map(d => ({
                date: d.timestamp * 1000,
                type: "ignitionOn",
                label: "Ignition On"
            }));

            const offData = (offDataRaw || []).map(d => ({
                date: d.timestamp * 1000,
                type: "ignitionOff",
                label: "Ignition Off"
            }));

            const allEvents = [...onData, ...offData].sort((a, b) => a.date - b.date);
            setEventsData(allEvents);
        } catch (error) {
            console.error('Error getting ignition events:', error);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    };

    const formatDateLabel = (timestamp) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

    const createChart = () => {
        const container = document.getElementById("fuelEventsChart");
        if (!container) return;

        if (rootRef.current) {
            rootRef.current.dispose();
            rootRef.current = null;
        }

        const root = am5.Root.new("fuelEventsChart");
        rootRef.current = root;
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: true,
                panY: true,
                wheelX: "panX",
                wheelY: "zoomX",
                pinchZoomX: true,
                layout: root.verticalLayout
            })
        );

        // X Axis
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: { timeUnit: "minute", count: 15 },
                renderer: am5xy.AxisRendererX.new(root, {
                    minGridDistance: 100,
                    minorGridEnabled: true
                }),
                tooltip: am5.Tooltip.new(root, {})
            })
        );

        // Custom date formatting for X-axis labels
        xAxis.get("renderer").labels.template.setAll({
            fontSize: 11,
            fill: am5.color(0x666666),
            rotation: -45,
            centerX: am5.p50,
            centerY: am5.p50,
            dy: 15
        });

        // Adapter for custom date format
        xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
            const date = target.dataItem?.date;
            if (date) {
                return formatDateLabel(date);
            }
            return text;
        });

        // Y Axis
        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
                title: am5.Label.new(root, {
                    text: "Fuel Volume (Liters)",
                    fontSize: 12,
                    fontWeight: "bold"
                }),
                min: 0,
                max: maxYAxis || 100,
                strictMinMax: true
            })
        );

        // Grid lines
        yAxis.get("renderer").grid.template.setAll({
            stroke: am5.color(0xe0e0e0),
            strokeWidth: 1,
            strokeDasharray: [5, 5]
        });

        xAxis.get("renderer").grid.template.setAll({
            stroke: am5.color(0xe0e0e0),
            strokeWidth: 1,
            strokeDasharray: [5, 5]
        });

        // Fuel Volume Series
        const fuelSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: "Fuel Volume",
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "rV",
                valueXField: "date",
                tooltip: am5.Tooltip.new(root, {
                    labelText: "Fuel: {valueY} L"
                    // \nDate: {formatTimestamp(date)}
                }),
                stroke: am5.color("#2196F3"),
                strokeWidth: 2,
                fill: am5.color("#2196F3"),
                fillOpacity: 0.1
            })
        );

        if (chartData.length > 0) {
            fuelSeries.data.setAll(chartData);
        }

        // Add bullets for fuel data points
        // if (chartData.length > 0) {
        //     fuelSeries.bullets.push(() => {
        //         return am5.Bullet.new(root, {
        //             sprite: am5.Circle.new(root, {
        //                 radius: 4,
        //                 fill: am5.color("#2196F3"),
        //                 stroke: am5.color(0xffffff),
        //                 strokeWidth: 2
        //             })
        //         });
        //     });
        // }

        // Process events and add markers
        if (eventsData && eventsData.length > 0) {
            addEventMarkers(eventsData, xAxis, chart, root, fuelSeries);
            addEventRanges(eventsData, xAxis, chart, root);
        }

        // Add cursor
        chart.set("cursor", am5xy.XYCursor.new(root, {}));

        // Add scrollbar
        chart.set("scrollbarX", am5.Scrollbar.new(root, {
            orientation: "horizontal"
        }));

        // Add legend
        const legend = chart.children.push(
            am5.Legend.new(root, {
                centerX: am5.p50,
                x: am5.p50,
                layout: root.horizontalLayout,
                marginTop: 10,
                marginBottom: 10
            })
        );

        const legendItems = [{ name: "Fuel Volume", fill: am5.color("#2196F3") }];
        
        if (eventsData.some(e => e.type === 'ignitionOn')) {
            legendItems.push({ name: "Ignition On", fill: am5.color("#38761d") });
        }
        if (eventsData.some(e => e.type === 'ignitionOff')) {
            legendItems.push({ name: "Ignition Off", fill: am5.color("#f44336") });
        }

        legend.data.setAll(legendItems);
        chart.appear(1000, 100);
    };

    const addEventRanges = (events, xAxis, chart, root) => {
        const ignitionOnEvents = events.filter(e => e.type === 'ignitionOn');
        const ignitionOffEvents = events.filter(e => e.type === 'ignitionOff');

        ignitionOnEvents.forEach(startEvent => {
            const endEvent = ignitionOffEvents.find(e => e.date > startEvent.date);
            if (endEvent) {
                const range = xAxis.createAxisRange(xAxis.makeDataItem({
                    value: startEvent.date,
                    endValue: endEvent.date
                }));
                range.get("axisFill").setAll({
                    fill: am5.color(0xFF9800),
                    fillOpacity: 0.15,
                    visible: true
                });
            }
        });
    };

    const addEventMarkers = (events, xAxis, chart, root, fuelSeries) => {
        const eventColors = {
            ignitionOn: "#6aa84f",
            ignitionOff: "#f44336"
        };

        events.forEach(event => {
            const date = event.date;
            const color = eventColors[event.type] || "#666666";

            // Create marker line
            const marker = xAxis.createAxisRange(xAxis.makeDataItem({
                value: date
            }));

            marker.get("grid").setAll({
                stroke: am5.color(color),
                strokeWidth: 2,
                strokeDasharray: [5, 5],
                visible: true
            });

            // Add label
            const label = marker.get("label");
            if (label) {
                label.setAll({
                    text: event.label || event.type,
                    fill: am5.color(color),
                    fontSize: 10,
                    fontWeight: "bold",
                    rotation: -90,
                    dy: 20,
                    dx: -10
                });
            }
        });
    };

    return (
        <div style={{ width: "100%", padding: "20px", background: "#fff", borderRadius: "10px" }}>
            <h3 style={{ marginBottom: "20px", color: "#333" }}>
                Fuel Volume with Event Markers
            </h3>
            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
            ) : chartData.length === 0 ? (
                <div style={{ textAlign: "center", padding: "50px", color: "#666" }}>
                    No fuel data available for the selected period
                </div>
            ) : (
                <div id="fuelEventsChart" style={{ width: "100%", height: "600px" }}></div>
            )}
        </div>
    );
};

export default FuelVolumeWithEventsChart;